import React, { useCallback, useEffect, useState } from 'react'
import SubtabNav from '../Workflow/SubtabNav'
import ChartSelector from '../ChartSelector'
import MobileMappingPanel from '../DataMapping/MobileMappingPanel'
import ChartPreviewWithOptions from '../ChartPreviewWithOptions'
import PreviewSizeControl from './PreviewSizeControl'
import WorkflowMobileSheet from './WorkflowMobileSheet'
import {
  ChartRecommendationPanel,
  AIAssistantPanel,
} from '../MvpAssistant/Panels'
import { MAP_TABS } from '../../config/workflow'
import { useDataContext } from '../../contexts/DataContext'
import { useChartContext } from '../../contexts/ChartContext'
import { useUIContext } from '../../contexts/UIContext'

export default function MapStep({ actions }) {
  const { filteredData: data, largeDatasetInfo } = useDataContext()

  const {
    charts,
    currentChart,
    activeRecommendation,
    mapping,
    setMapping,
    visualOptions,
    setVisualOptions,
    setRawViz,
    mappingLoading,
    setMappingLoading,
    brandTheme,
    chartColorSyncMode,
    chartColorOptionMeta,
  } = useChartContext()

  const {
    previewDimensions,
    previewSizePreset,
    setPreviewSizePreset,
    previewCustomSize,
    setPreviewCustomSize,
    previewSizePresets,
    activeMapTab,
    setActiveMapTab,
    previewRendererMode,
    recommendations,
    aiResult,
    handleRecommendationSelect,
    handleMapChartSelection,
  } = useUIContext()

  const datasetRowCount = largeDatasetInfo.rowCount

  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  )

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const handleChange = () => setIsMobileLayout(mediaQuery.matches)

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }

    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  const [pickerDimension, setPickerDimension] = useState(null)

  const handleOpenPicker = useCallback((dim) => setPickerDimension(dim), [])
  const handleClosePicker = useCallback(() => setPickerDimension(null), [])

  // Reset picker when navigating away from the mapping tab
  useEffect(() => {
    if (activeMapTab !== 'mapping') setPickerDimension(null)
  }, [activeMapTab])

  const assistContent = (
    <>
      <ChartRecommendationPanel
        recommendations={recommendations}
        charts={charts}
        onSelectChart={(chart) => {
          const recommendation = recommendations.find(
            (item) => item.chartKey === chart.metadata.id
          )
          handleRecommendationSelect(chart, recommendation)
        }}
      />
      <AIAssistantPanel aiResult={aiResult} />
    </>
  )

  const panelContent = (isMobileLayout) => (
    <>
      {activeMapTab === 'chart' && (
        <ChartSelector
          availableCharts={charts}
          currentChart={currentChart}
          setCurrentChart={handleMapChartSelection}
        />
      )}
      {activeMapTab === 'mapping' && (
        <>
          {!isMobileLayout && activeRecommendation?.chartKey === currentChart.metadata.id &&
            activeRecommendation?.optionHints?.length > 0 && (
              <div className="alert alert-info py-2">
                <strong>Recommended setup</strong>
                <div className="small mt-1">
                  {activeRecommendation?.optionHints[0]}
                </div>
              </div>
            )}
          <MobileMappingPanel
            dimensions={currentChart.dimensions}
            dataTypes={data.dataTypes}
            mapping={mapping}
            setMapping={setMapping}
            currentChart={currentChart}
            onSwitchToChart={() => setActiveMapTab('chart')}
            pickerDimension={pickerDimension}
            onOpenPicker={handleOpenPicker}
            onClosePicker={handleClosePicker}
            bleedToEdge={isMobileLayout}
            inSheet={isMobileLayout}
          />
        </>
      )}
      {activeMapTab === 'assist' && assistContent}
    </>
  )

  // When the picker is open on mobile, replace the tab bar with a back button + title
  const tabsOverride =
    activeMapTab === 'mapping' && pickerDimension
      ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 2px', width: '100%', height: 50, position: 'relative' }}>
            <button
              type="button"
              onClick={handleClosePicker}
              style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--primary)', fontSize: 15, fontWeight: 600, padding: '8px 8px' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 6l-6 6 6 6" />
              </svg>
              Back
            </button>
            <span style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', fontSize: 15, fontWeight: 600, pointerEvents: 'none' }}>
              {pickerDimension.multiple ? 'Add to ' : ''}{pickerDimension.name}
            </span>
          </div>
        )
      : undefined

  const renderPreview = () => (
    <ChartPreviewWithOptions
      chart={currentChart}
      dataset={data.dataset}
      dataTypes={data.dataTypes}
      mapping={mapping}
      visualOptions={visualOptions}
      setVisualOptions={setVisualOptions}
      setRawViz={setRawViz}
      datasetRowCount={datasetRowCount}
      previewRendererMode={previewRendererMode}
      loading={mappingLoading}
      setMappingLoading={setMappingLoading}
      brandTheme={brandTheme}
      chartColorSyncMode={chartColorSyncMode}
      hasMappedColorField={chartColorOptionMeta.hasMappedColorField}
      showOptions={false}
      previewClassName="col-12"
    />
  )

  if (isMobileLayout) {
    return (
      <WorkflowMobileSheet
        mainContent={renderPreview()}
        topStrip={
          <PreviewSizeControl
            previewSizePreset={previewSizePreset}
            setPreviewSizePreset={setPreviewSizePreset}
            previewCustomSize={previewCustomSize}
            setPreviewCustomSize={setPreviewCustomSize}
            previewDimensions={previewDimensions}
            presets={previewSizePresets}
          />
        }
        tabs={MAP_TABS}
        activeTab={activeMapTab}
        onTabChange={setActiveMapTab}
        sheetContent={panelContent(true)}
        actions={actions}
        tabsOverride={tabsOverride}
      />
    )
  }

  return (
    <>
      <div className="workflow-step-header">
        <div className="workflow-step-copy">
          <h2>Map</h2>
          <p>
            Choose a chart, map the right fields, and keep the preview close
            while you work.
          </p>
        </div>
        <div className="workflow-step-actions-desktop">{actions}</div>
      </div>
      <SubtabNav
        tabs={MAP_TABS}
        activeTab={activeMapTab}
        onTabChange={setActiveMapTab}
      />
      <div className="workflow-split">
        <div className="workflow-panel workflow-panel-left">
          {panelContent(false)}
        </div>
        <div className="workflow-panel workflow-panel-preview">
          {renderPreview()}
          <PreviewSizeControl
            previewSizePreset={previewSizePreset}
            setPreviewSizePreset={setPreviewSizePreset}
            previewCustomSize={previewCustomSize}
            setPreviewCustomSize={setPreviewCustomSize}
            previewDimensions={previewDimensions}
            presets={previewSizePresets}
          />
        </div>
      </div>
      <div className="workflow-step-actions-mobile">{actions}</div>
    </>
  )
}
